import { useCallback, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Calculator as CalcIcon, Copy, ArrowUpRight } from "lucide-react";

/* ASME B16.5 flange bolting. Class 150 & 300 include published RF / RTJ stud lengths;
   higher classes list quantity + diameter only — length comes from the joint stack-up. */
const BOLTING = {
  150: [
    ["½", 4, "½", '2¼', null], ["¾", 4, "½", "2½", null], ["1", 4, "½", "2½", "3"],
    ["1¼", 4, "½", "2¾", "3¼"], ["1½", 4, "½", "2¾", "3¼"], ["2", 4, "⅝", "3¼", "3¾"],
    ["2½", 4, "⅝", "3½", "4"], ["3", 4, "⅝", "3½", "4"], ["3½", 8, "⅝", "3½", "4"],
    ["4", 8, "⅝", "3½", "4"], ["5", 8, "¾", "3¾", "4¼"], ["6", 8, "¾", "4", "4½"],
    ["8", 8, "¾", "4¼", "4¾"], ["10", 12, "⅞", "4½", "5"], ["12", 12, "⅞", "4¾", "5¼"],
    ["14", 12, "1", "5¼", "5¾"], ["16", 16, "1", "5¼", "5¾"], ["18", 16, "1⅛", "5¾", "6¼"],
    ["20", 20, "1⅛", "6¼", "6¾"], ["24", 20, "1¼", "6¾", "7¼"],
  ],
  300: [
    ["½", 4, "½", "2½", "3"], ["¾", 4, "⅝", "3", "3½"], ["1", 4, "⅝", "3", "3½"],
    ["1¼", 4, "⅝", "3¼", "3¾"], ["1½", 4, "¾", "3½", "4"], ["2", 8, "⅝", "3½", "4"],
    ["2½", 8, "¾", "4", "4½"], ["3", 8, "¾", "4¼", "4¾"], ["3½", 8, "¾", "4¼", "5"],
    ["4", 8, "¾", "4½", "5"], ["5", 8, "¾", "4¾", "5¼"], ["6", 12, "¾", "4¾", "5½"],
    ["8", 12, "⅞", "5½", "6"], ["10", 16, "1", "6¼", "6¾"], ["12", 16, "1⅛", "6¾", "7¼"],
    ["14", 20, "1⅛", "7", "7½"], ["16", 20, "1¼", "7½", "8"], ["18", 24, "1¼", "7¾", "8¼"],
    ["20", 24, "1¼", "8", "8¾"], ["24", 24, "1½", "9", "10"],
  ],
  400: [
    ["½", 4, "½"], ["¾", 4, "⅝"], ["1", 4, "⅝"], ["1¼", 4, "⅝"], ["1½", 4, "¾"],
    ["2", 8, "⅝"], ["2½", 8, "¾"], ["3", 8, "¾"], ["3½", 8, "⅞"], ["4", 8, "⅞"],
    ["5", 8, "⅞"], ["6", 12, "⅞"], ["8", 12, "1"], ["10", 16, "1⅛"], ["12", 16, "1¼"],
    ["14", 20, "1¼"], ["16", 20, "1⅜"], ["18", 24, "1⅜"], ["20", 24, "1½"], ["24", 24, "1¾"],
  ],
  600: [
    ["½", 4, "½"], ["¾", 4, "⅝"], ["1", 4, "⅝"], ["1¼", 4, "⅝"], ["1½", 4, "¾"],
    ["2", 8, "⅝"], ["2½", 8, "¾"], ["3", 8, "¾"], ["3½", 8, "⅞"], ["4", 8, "⅞"],
    ["5", 8, "1"], ["6", 12, "1"], ["8", 12, "1⅛"], ["10", 16, "1¼"], ["12", 20, "1¼"],
    ["14", 20, "1⅜"], ["16", 20, "1½"], ["18", 20, "1⅝"], ["20", 24, "1⅝"], ["24", 24, "1⅞"],
  ],
  900: [
    ["½", 4, "¾"], ["¾", 4, "¾"], ["1", 4, "⅞"], ["1¼", 4, "⅞"], ["1½", 4, "1"],
    ["2", 8, "⅞"], ["2½", 8, "1"], ["3", 8, "⅞"], ["4", 8, "1⅛"], ["5", 8, "1¼"],
    ["6", 12, "1⅛"], ["8", 12, "1⅜"], ["10", 16, "1⅜"], ["12", 20, "1⅜"], ["14", 20, "1½"],
    ["16", 20, "1⅝"], ["18", 20, "1⅞"], ["20", 20, "2"], ["24", 20, "2½"],
  ],
  1500: [
    ["½", 4, "¾"], ["¾", 4, "¾"], ["1", 4, "⅞"], ["1¼", 4, "⅞"], ["1½", 4, "1"],
    ["2", 8, "⅞"], ["2½", 8, "1"], ["3", 8, "1⅛"], ["4", 8, "1¼"], ["5", 8, "1½"],
    ["6", 12, "1⅜"], ["8", 12, "1⅝"], ["10", 12, "1⅞"], ["12", 16, "2"], ["14", 16, "2¼"],
    ["16", 16, "2½"], ["18", 16, "2¾"], ["20", 16, "3"], ["24", 16, "3½"],
  ],
  2500: [
    ["½", 4, "¾"], ["¾", 4, "¾"], ["1", 4, "⅞"], ["1¼", 4, "1"], ["1½", 4, "1⅛"],
    ["2", 8, "1"], ["2½", 8, "1⅛"], ["3", 8, "1¼"], ["4", 8, "1½"], ["5", 8, "1¾"],
    ["6", 8, "2"], ["8", 12, "2"], ["10", 12, "2½"], ["12", 12, "2¾"],
  ],
};

const METRIC = {
  "½": "M12", "⅝": "M16", "¾": "M20", "⅞": "M22", "1": "M24", "1⅛": "M27",
  "1¼": "M30", "1⅜": "M33", "1½": "M36", "1⅝": "M39", "1¾": "M42", "1⅞": "M45",
  "2": "M48", "2¼": "M56", "2½": "M64", "2¾": "M70", "3": "M72", "3½": "M85",
};

const SERVICES = {
  general: {
    label: "General / carbon steel",
    stud: "ASTM A193 Gr. B7",
    nut: "ASTM A194 Gr. 2H",
    note: "Cr-Mo alloy steel, service up to 500 °C. The standard refinery and utility bolting.",
  },
  sour: {
    label: "Sour service (NACE)",
    stud: "ASTM A193 Gr. B7M",
    nut: "ASTM A194 Gr. 2HM",
    note: "Controlled hardness for wet H₂S service — confirm against your NACE / project spec.",
  },
  lowtemp: {
    label: "Low temperature",
    stud: "ASTM A320 Gr. L7",
    nut: "ASTM A194 Gr. 4 or 7",
    note: "Impact-tested bolting for cryogenic and sub-zero duty.",
  },
  ss304: {
    label: "Stainless 304",
    stud: "ASTM A193 Gr. B8 Cl.2",
    nut: "ASTM A194 Gr. 8",
    note: "Strain-hardened 304. Use anti-seize to avoid thread galling.",
  },
  ss316: {
    label: "Stainless 316 / chloride",
    stud: "ASTM A193 Gr. B8M Cl.2",
    nut: "ASTM A194 Gr. 8M",
    note: "316 for chloride-bearing and marine environments.",
  },
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-[11px] tracking-[0.14em] uppercase text-steel font-bold mb-2">
      {label}
    </span>
    {children}
  </label>
);

const selectCls =
  "w-full bg-white border border-steel-light rounded-lg text-ink text-sm font-semibold px-4 py-3 outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 transition-all duration-200";

const Readout = ({ label, value, sub, accent }) => (
  <div
    className={`rounded-xl border px-5 py-4 ${
      accent ? "border-brand/40 bg-brand/5" : "border-steel-light bg-white"
    }`}
  >
    <div className="text-[11px] tracking-[0.14em] uppercase text-steel font-bold">{label}</div>
    <div className="font-display font-extrabold text-xl sm:text-2xl text-ink leading-none mt-2 tabular-nums">
      {value}
    </div>
    {sub && <div className="font-sans font-semibold text-[11px] text-steel mt-2">{sub}</div>}
  </div>
);

const FADE_UP = { opacity: 0, y: 22 };
const FADE_IN = { opacity: 1, y: 0 };
const PANEL_VIEWPORT = { once: true, amount: 0.2 };
const PANEL_TRANSITION = { duration: 0.6 };
const PANEL_TRANSITION_DELAYED = { duration: 0.6, delay: 0.1 };
const HEAD_HIDDEN = { opacity: 0, y: 20 };
const HEAD_VIEWPORT = { once: true, amount: 0.3 };
const FACINGS = ["RF", "RTJ"];
const COARSE_DIAMETERS = ["½", "⅝", "¾", "⅞"];

function useBoltingSpec({ cls, nps, facing, service }) {
  const sizes = BOLTING[cls];
  const row = useMemo(() => sizes.find((r) => r[0] === nps) ?? sizes[0], [sizes, nps]);

  return useMemo(() => {
    const [size, qty, dia, rf, rtj] = row;
    const length = facing === "RTJ" ? rtj : rf;
    const svc = SERVICES[service];
    const threadSeries = COARSE_DIAMETERS.includes(dia) ? "UNC" : "8UN";

    const spec = `ASME B16.5 Class ${cls} · NPS ${size} · ${facing} facing
Stud bolts: ${qty} nos — ${dia}" (${METRIC[dia] ?? "—"}) ${threadSeries}${
      length ? ` × ${length}" long` : " × length per joint stack-up"
    }
Nuts: ${qty * 2} nos heavy hex
Stud grade: ${svc.stud}   |   Nut grade: ${svc.nut}
Service: ${svc.label}`;

    const waHref = `https://wa.me/918000151777?text=${encodeURIComponent(
      `Hello Rahil Engineering Works, I need a quote for:

${spec}

Please confirm price and lead time.`
    )}`;

    return { sizes, size, qty, dia, length, svc, threadSeries, spec, waHref };
  }, [row, sizes, cls, facing, service]);
}

const JointInputs = ({ cls, setCls, nps, setNps, facing, setFacing, service, setService, sizes }) => (
  <motion.div
    initial={FADE_UP}
    whileInView={FADE_IN}
    viewport={PANEL_VIEWPORT}
    transition={PANEL_TRANSITION}
    className="lg:col-span-5 bg-mist border border-steel-light rounded-2xl p-6 lg:p-7 space-y-5"
    data-testid="calculator-inputs"
  >
    <div className="flex items-center gap-3 pb-1">
      <span className="w-10 h-10 rounded-xl bg-navy-deep text-white flex items-center justify-center">
        <CalcIcon size={18} />
      </span>
      <span className="font-display font-bold uppercase text-base text-ink">Joint details</span>
    </div>

    <Field label="Flange standard">
      <input
        value={'ASME B16.5 (NPS ½ – 24)'}
        readOnly
        className={`${selectCls} text-steel cursor-default`}
        data-testid="calc-standard"
      />
    </Field>

    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Pressure class">
        <select
          value={cls}
          onChange={(e) => {
            const next = e.target.value;
            setCls(next);
            if (!BOLTING[next].some((r) => r[0] === nps)) setNps(BOLTING[next][0][0]);
          }}
          className={selectCls}
          data-testid="calc-class-select"
        >
          {Object.keys(BOLTING).map((c) => (
            <option key={c} value={c}>
              Class {c}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Nominal pipe size">
        <select
          value={nps}
          onChange={(e) => setNps(e.target.value)}
          className={selectCls}
          data-testid="calc-nps-select"
        >
          {sizes.map((r) => (
            <option key={r[0]} value={r[0]}>
              NPS {r[0]}"
            </option>
          ))}
        </select>
      </Field>
    </div>

    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Flange facing">
        <div className="flex gap-2">
          {FACINGS.map((f) => (
            <button
              key={f}
              onClick={() => setFacing(f)}
              data-testid={`calc-facing-${f.toLowerCase()}`}
              className={`flex-1 text-[11px] tracking-[0.14em] uppercase font-bold py-3 rounded-lg border transition-colors duration-300 ${
                facing === f
                  ? "bg-navy border-navy text-white"
                  : "bg-white border-steel-light text-navy hover:border-navy"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </Field>

      <Field label="Service condition">
        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          className={selectCls}
          data-testid="calc-service-select"
        >
          {Object.entries(SERVICES).map(([k, v]) => (
            <option key={k} value={k}>
              {v.label}
            </option>
          ))}
        </select>
      </Field>
    </div>

    <p className="font-sans font-semibold text-[11px] leading-relaxed text-steel pt-1">
      Lengths are published ASME B16.5 flange-to-flange values (first thread to
      first thread). For blinds, valves, spacers, isolation kits or unequal
      flanges, confirm from the joint stack-up.
    </p>
  </motion.div>
);

const GradeCard = ({ label, value, testid }) => (
  <div className="rounded-xl border border-steel-light bg-white px-5 py-4">
    <div className="text-[11px] tracking-[0.14em] uppercase text-steel font-bold">{label}</div>
    <div className="font-display font-bold text-lg text-ink mt-2" data-testid={testid}>
      {value}
    </div>
  </div>
);

const SpecOutput = ({ cls, facing, bolting, onSendToRfq, onCopy }) => {
  const { size, qty, dia, length, svc, threadSeries, spec, waHref } = bolting;

  return (
    <motion.div
      initial={FADE_UP}
      whileInView={FADE_IN}
      viewport={PANEL_VIEWPORT}
      transition={PANEL_TRANSITION_DELAYED}
      className="lg:col-span-7"
      data-testid="calculator-output"
    >
      <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Readout label="Stud bolts" value={`${qty} nos`} sub={`${qty * 2} heavy hex nuts`} accent />
        <Readout
          label="Stud diameter"
          value={`${dia}"`}
          sub={`${METRIC[dia] ?? "—"} · ${threadSeries}`}
        />
        <Readout
          label={`Stud length (${facing})`}
          value={length ? `${length}"` : "Stack-up"}
          sub={length ? "First thread to first thread" : "Class > 300 — confirm from joint"}
        />
        <Readout label="Bolt circle" value={`Class ${cls}`} sub={`NPS ${size}" flange`} />
      </div>

      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <GradeCard label="Recommended stud grade" value={svc.stud} testid="calc-stud-grade" />
        <GradeCard label="Matching nut grade" value={svc.nut} testid="calc-nut-grade" />
      </div>

      <div className="mt-3 rounded-xl bg-navy-deep text-white px-5 py-5">
        <div className="text-[11px] tracking-[0.14em] uppercase text-blue-300 font-bold mb-3">
          Enquiry summary
        </div>
        <pre
          className="font-sans font-semibold text-[11px] leading-relaxed text-blue-50 whitespace-pre-wrap"
          data-testid="calc-spec-text"
        >
          {spec}
        </pre>
        <p className="text-[11px] text-slate-400 mt-3 leading-relaxed">{svc.note}</p>

        <div className="mt-5 flex flex-wrap gap-2.5">
          <button
            onClick={onSendToRfq}
            data-testid="calc-send-rfq-btn"
            className="inline-flex items-center gap-2 bg-brand text-white text-[11px] tracking-[0.14em] uppercase font-bold px-5 py-3 rounded-lg hover:bg-white hover:text-navy transition-colors duration-300"
          >
            Add to RFQ
            <ArrowUpRight size={14} />
          </button>
          <a
            href={waHref}
            target="_blank"
            rel="noreferrer"
            data-testid="calc-whatsapp-btn"
            className="inline-flex items-center gap-2 border border-slate-600 text-[11px] tracking-[0.14em] uppercase font-bold px-5 py-3 rounded-lg hover:border-brand hover:text-blue-200 transition-colors duration-300"
          >
            Send on WhatsApp
          </a>
          <button
            onClick={onCopy}
            data-testid="calc-copy-btn"
            className="inline-flex items-center gap-2 border border-slate-600 text-[11px] tracking-[0.14em] uppercase font-bold px-5 py-3 rounded-lg hover:border-brand hover:text-blue-200 transition-colors duration-300"
          >
            <Copy size={13} />
            Copy
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default function Calculator() {
  const [cls, setCls] = useState("150");
  const [nps, setNps] = useState("4");
  const [facing, setFacing] = useState("RF");
  const [service, setService] = useState("general");

  const bolting = useBoltingSpec({ cls, nps, facing, service });

  const sendToRfq = useCallback(() => {
    window.dispatchEvent(new CustomEvent("rew:prefill-rfq", { detail: bolting.spec }));
    toast.success("Specification added to the RFQ form below.");
    document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
  }, [bolting.spec]);

  const copy = useCallback(async () => {
    await navigator.clipboard.writeText(bolting.spec);
    toast.success("Specification copied to clipboard.");
  }, [bolting.spec]);

  return (
    <section
      id="calculator"
      className="relative bg-white border-y border-steel-light py-20 lg:py-28"
      data-testid="calculator-section"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-10">
        <motion.div
          initial={HEAD_HIDDEN}
          whileInView={FADE_IN}
          viewport={HEAD_VIEWPORT}
          transition={PANEL_TRANSITION}
          className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10"
        >
          <div>
            <div className="flex items-center gap-3 text-[11px] sm:text-xs tracking-[0.12em] uppercase text-steel font-semibold mb-4">
              <span className="w-10 h-[2px] bg-brand" />
              Engineer's Tool
            </div>
            <h2 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-4xl sm:text-5xl text-ink">
              Flange bolting
              <br />
              size calculator
            </h2>
          </div>
          <p className="max-w-sm text-sm text-slate-600 leading-relaxed">
            Pick the flange class, size and service — get the stud quantity,
            diameter, length and matching nut grade instantly, then send it
            straight to us as an enquiry.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-12 gap-6 lg:gap-8">
          <JointInputs
            cls={cls}
            setCls={setCls}
            nps={nps}
            setNps={setNps}
            facing={facing}
            setFacing={setFacing}
            service={service}
            setService={setService}
            sizes={bolting.sizes}
          />
          <SpecOutput
            cls={cls}
            facing={facing}
            bolting={bolting}
            onSendToRfq={sendToRfq}
            onCopy={copy}
          />
        </div>
      </div>
    </section>
  );
}
